import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { throwError } from 'rxjs';
import { AuthService } from './auth.service';

/**
 * Intercepteur JWT :
 *  - Ajoute l'header Authorization si un token est présent.
 *  - Si le token est expiré côté client (claim exp dépassée), déclenche la
 *    déconnexion automatique avant même d'émettre la requête → évite les
 *    aller-retours 401 inutiles et garantit une UX cohérente.
 *  - N'intervient pas sur les routes publiques d'authentification.
 */
export const jwtInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  // Ne pas exiger de token pour les routes publiques (login, register, OTP…)
  // Inclure les endpoints d'authentification publics, y compris le reset_password
  const isAuthRoute = /\/x-auth\/(login|register|otp\/request|verify-account|otp\/verify|reset_password)/.test(
    req.url,
  );
  if (isAuthRoute) {
    return next(req);
  }

  const token = auth.getToken();
  if (token && auth.isTokenExpired(token)) {
    // Token expiré côté client → on coupe court
    auth.logout('expired');
    return throwError(
      () =>
        new HttpErrorResponse({
          status: 401,
          statusText: 'Token expired',
          url: req.url,
        }),
    );
  }

  // En-tête Accept-Language : résout la langue côté backend pour les
  // messages d'erreur et les notifications SMS/Email envoyées au client.
  // Lecture directe depuis le localStorage pour éviter une dépendance
  // circulaire avec LocaleService.
  let lang: string | null = null;
  try {
    lang = localStorage.getItem('dinki_locale');
  } catch {
    /* SSR */
  }

  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (lang) headers['Accept-Language'] = lang;

  // Les routes du portail customer sont publiques (magic-link) → pas de redirect login
  const isPublicPortal = /\/portal\/c\//.test(req.url);

  if (token) {
    req = req.clone({ setHeaders: headers });
  } else {
    if (lang) req = req.clone({ setHeaders: { 'Accept-Language': lang } });
    if (!isPublicPortal) {
      router.navigate(['/authentication/login']);
    }
  }
  return next(req);
};
